import { FileSource, UploadedFile } from '@/domain/file'
import { Repayment, Repayments } from '@/domain/repayments'
import { AuthService } from '@/service/auth.service'
import { QueryParamsService } from '@/service/query-params.service'
import { RepaymentsTableQueryService } from '@/service/repayments-table-query.service'
import { RepaymentsService } from '@/service/repayments.service'
import { UploadedFileService } from '@/service/uploaded-file.service'
import { CommonModule } from '@angular/common'
import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router'
import { ConfirmationService, MessageService } from 'primeng/api'
import { BadgeModule } from 'primeng/badge'
import { ButtonModule } from 'primeng/button'
import { ConfirmPopupModule } from 'primeng/confirmpopup'
import { DatePickerModule } from 'primeng/datepicker'
import { EditorModule } from 'primeng/editor'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import { InputTextModule } from 'primeng/inputtext'
import { ListboxModule } from 'primeng/listbox'
import { Popover, PopoverModule } from 'primeng/popover'
import { SelectModule } from 'primeng/select'
import { SkeletonModule } from 'primeng/skeleton'
import { SplitterModule } from 'primeng/splitter'
import { Table, TableModule } from 'primeng/table'
import { TabsModule } from 'primeng/tabs'
import { ToastModule } from 'primeng/toast'
import { TooltipModule } from 'primeng/tooltip'
import { Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'
import { parseEmbedPermissions } from '../../embed-permissions'
import { UploadedFileRowComponent } from '../recent-uploads/uploaded-file-row/recent-uploads.component'
import { RepaymentsTableComponent } from '../repayments-table/repayments-table.component'

const DEFAULT_SORT_ORDER = 1
const DEFAULT_SORT_FIELD = 'repayment_date'
type QuickFilterOption = {
  id?: string
  name: string
  merchantId?: string
  cashAdvanceId?: string
  activeCashAdvance?: boolean
}
type QuickFilterMerchantContext = {
  id?: string
  name?: string
}
type QuickFilterKind = 'merchant' | 'terminal' | 'cashAdvance'
interface AppliedQuickFilterPill {
  kind: QuickFilterKind
  icon: string
  label: string
}

interface QuickFilterSearchState {
  term: string
  loading: boolean
  loadingMore: boolean
  searched: boolean
  results: QuickFilterOption[]
  options: QuickFilterOption[]
  error: string | null
  debounceHandle: ReturnType<typeof setTimeout> | null
  scrollCleanup: (() => void) | null
}
type TableFilterConstraint = {
  value: boolean
  matchMode: 'equals'
  operator: 'and'
}

@Component({
  selector: 'repayments-view',
  templateUrl: 'repayments-view.component.html',
  standalone: true,
  styleUrl: 'repayments-view.component.scss',
  imports: [
    CommonModule,
    TableModule,
    BadgeModule,
    ButtonModule,
    InputTextModule,
    InputIconModule,
    IconFieldModule,
    FormsModule,
    EditorModule,
    SkeletonModule,
    TooltipModule,
    ToastModule,
    ConfirmPopupModule,
    DatePickerModule,
    UploadedFileRowComponent,
    RepaymentsTableComponent,
    ListboxModule,
    SelectModule,
    SplitterModule,
    TabsModule,
    PopoverModule,
  ],
  providers: [ConfirmationService, MessageService],
})
export class RepaymentsViewComponent implements OnInit, OnDestroy {
  @ViewChildren(Popover) popovers!: QueryList<Popover>
  @ViewChild(RepaymentsTableComponent)
  repaymentsTableComponent?: RepaymentsTableComponent
  private static instanceCount = 0
  private readonly instanceId = ++RepaymentsViewComponent.instanceCount
  sourceFileId!: string | null
  country!: string | null
  showAll: boolean = false
  embedded: boolean | null = null
  canView: boolean = true
  canEdit: boolean = true
  canDelete: boolean = true
  sourceFile!: UploadedFile | null
  private sourceFileStatusBeforeEdits: string | null = null
  public selectedRepayments!: Repayments
  private readonly committingFileIds = new Set<string>()
  private readonly committingFileProgress = new Map<string, number>()
  private commitProgressPoll: ReturnType<typeof setInterval> | null = null

  quickFilterVisible: boolean = false
  quickFilter: string = ''

  statuses!: any[]
  searchValue!: string
  loading: boolean = true

  sortField: string = DEFAULT_SORT_FIELD
  sortOrder: number = DEFAULT_SORT_ORDER

  activityValues: number[] = [0, 100]

  hasActiveFilters: boolean = false

  showDeletedOnly: boolean = false

  public dataTable: Table | undefined

  public repayments: Repayments = []

  public selectedMerchants: QuickFilterOption[] = []
  public selectedTerminals: QuickFilterOption[] = []
  public selectedCashAdvances: QuickFilterOption[] = []
  private quickFilterContextRepayments: Repayments = []
  private quickFilterContextTerminalOptions: QuickFilterOption[] = []
  private quickFilterContextCashAdvanceOptions: QuickFilterOption[] = []
  private quickFilterContextKey: string | null = null
  private quickFilterContextLoadId = 0
  private quickFilterContextLoaded = false
  private quickFilterContextLoading = false
  private appliedQuickFilterOrder: string[] = []
  private hoveredAppliedQuickFilterKey: string | null = null
  private hoveredAppliedQuickFilterWidth: number | null = null
  public merchantSearch = this.createQuickFilterSearchState()
  public terminalSearch = this.createQuickFilterSearchState()
  public cashAdvanceSearch = this.createQuickFilterSearchState()

  public repaymentsTotal: number | null = null

  public popupDate: Date | undefined

  public currentPage: string = ''

  columns = [
    'description',
    'merchant_id',
    'cash_advance_id',
    'terminal_id',
    'repayment_date',
    'payment_amount',
    'updated_by',
    'deleted',
  ]

  filterCriteria: Array<{ name: string; column: string }> = [
    { name: 'Merchants', column: '' },
    { name: 'Terminals', column: '' },
    { name: 'Cash Advances', column: '' },
  ]
  selectedFilterCriteria!: { name: string; column: string }
  private readonly destroy$ = new Subject<void>()
  private readonly lifecycleAbort = new AbortController()
  private activeFilteredRepaymentsAbort: AbortController | null = null
  private destroyed = false
  private loadCounter = 0
  private activeLoadId = 0
  private loadingAllRepayments = false
  private readonly quickFilterPopovers: Partial<
    Record<QuickFilterKind, Popover>
  > = {}
  private readonly quickFilterRouteSyncPending: Partial<
    Record<QuickFilterKind, boolean>
  > = {}

  private get selectedRepaymentsSource(): 'repayments' | 'repayments_staging' {
    return this.currentPage === 'in-progress'
      ? 'repayments_staging'
      : 'repayments'
  }
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private repaymentsService: RepaymentsService,
    private authService: AuthService,
    private uploadedFileService: UploadedFileService,
    private confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly queryParamsService: QueryParamsService,
    private readonly tableQueryService: RepaymentsTableQueryService,
  ) {}

  ngOnInit() {
    if (this.instanceId > 1) {
      console.warn(
        `[RepaymentsViewComponent] duplicate instance detected (${this.instanceId})`,
      )
    }
    this.loading = true
    this.commitProgressPoll = setInterval(() => {
      void this.refreshSourceFileCommitProgress()
    }, 1500)
    this.tableQueryService.setPagination(0, 100)
    this.applyDefaultDeletedFilter()
    this.currentPage =
      this.getDeepestChild(this.route).snapshot.routeConfig?.path ?? ''
    this.embedded = this.currentPage === 'embed-view'
    this.applyPermissionsFromParams(this.route.snapshot.queryParams || {})
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        if (this.destroyed) {
          return
        }
        const previousPage = this.currentPage
        const childRoute = this.getDeepestChild(this.route)
        this.currentPage = childRoute.snapshot.routeConfig?.path ?? ''
        this.repaymentsService.repaymentsSource = this.selectedRepaymentsSource
        if (
          this.shouldResetForRoutePageChange(previousPage, this.currentPage)
        ) {
          this.prepareRepaymentsLoadingState()
        }
        if (!this.canView) {
          this.clearRepaymentsForNoView()
          return
        }
        const routePageChanged = previousPage !== this.currentPage
        const needsInitialLoad =
          !this.sourceFileId &&
          (this.currentPage === 'repayments' ||
            this.currentPage === 'in-progress') &&
          this.repayments.length === 0
        if (routePageChanged || needsInitialLoad) {
          setTimeout(() => this.ensureRepaymentsLoaded(), 0)
        }
      })

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        if (this.isRoutePathChanging(event.url)) {
          this.closeAllPopovers()
        }
        const nextPage = this.getRoutePageFromUrl(event.url)
        if (this.shouldResetForRoutePageChange(this.currentPage, nextPage)) {
          this.prepareRepaymentsLoadingState()
        }
      })
    this.queryParamsService.queryParams$
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (this.destroyed) {
          return
        }

        this.applyPermissionsFromParams(params)

        if (!this.canView) {
          this.selectedMerchants = []
          this.selectedCashAdvances = []
          this.selectedTerminals = []
          this.clearRepaymentsForNoView()
          return
        }

        if (params['merchants(name)']) {
          this.selectedMerchants = this.parseQuickFilterParams(
            'merchant',
            params['merchants(name)'],
          )
        } else {
          this.selectedMerchants = []
        }

        if (params['cash_advances(name)']) {
          this.selectedCashAdvances = this.parseQuickFilterParams(
            'cashAdvance',
            params['cash_advances(name)'],
          )
        } else {
          this.selectedCashAdvances = []
        }
        if (params['terminals(name)']) {
          this.selectedTerminals = this.parseQuickFilterParams(
            'terminal',
            params['terminals(name)'],
          )
        } else {
          this.selectedTerminals = []
        }
        this.reconcileQuickFilterSelections()
        this.syncAppliedQuickFilterOrder()
        void this.syncQuickFilterContextOptions().finally(() => {
          if (this.destroyed) {
            return
          }
          this.refreshQuickFilterOptions('merchant')
          this.refreshQuickFilterOptions('terminal')
          this.refreshQuickFilterOptions('cashAdvance')
        })

        const previousCountry = this.country ?? null
        const previousShowAll =
          typeof this.showAll === 'boolean' ? this.showAll : null
        const previousSourceFileId = this.sourceFileId ?? null

        const getSingleParamValue = (
          value: string | string[] | null | undefined,
        ): string | null => {
          if (Array.isArray(value)) {
            return value.length > 0 ? (value[0] ?? null) : null
          }
          return value ?? null
        }

        const nextCountry = getSingleParamValue(params['country'])
        const nextSourceFileId = getSingleParamValue(params['sourceFileId'])
        const showAllParam = getSingleParamValue(params['showAll'])
        const nextShowAll = showAllParam === 'true'

        const childRoute = this.getDeepestChild(this.route)
        this.currentPage = childRoute.snapshot.routeConfig?.path ?? ''
        this.embedded = this.currentPage === 'embed-view'
        this.repaymentsService.repaymentsSource = this.selectedRepaymentsSource

        const serviceSourceFileId = this.repaymentsService.sourceFileId ?? null

        const countryChanged =
          previousCountry !== null && nextCountry !== previousCountry
        const showAllChanged =
          previousShowAll !== null && nextShowAll !== previousShowAll
        const sourceFileChanged =
          previousSourceFileId !== nextSourceFileId &&
          !(previousSourceFileId === null && nextSourceFileId === null)
        const serviceContextChanged = serviceSourceFileId !== nextSourceFileId

        if (
          countryChanged ||
          showAllChanged ||
          sourceFileChanged ||
          serviceContextChanged
        ) {
          this.resetTableState()
        }

        this.country = nextCountry
        this.sourceFileId = nextSourceFileId
        this.showAll = nextShowAll

        if (!this.canView) {
          this.clearRepaymentsForNoView()
          return
        }

        if (
          nextCountry === 'ie' ||
          this.currentPage === 'merchants' ||
          this.currentPage === 'terminals' ||
          this.currentPage === 'cash-advances'
        ) {
          setTimeout(() => this.ensureRepaymentsLoaded(), 0)
          return
        }

        if (nextSourceFileId) {
          this.loadRepaymentsFromFile(nextSourceFileId)
          return
        }

        if (
          countryChanged ||
          showAllChanged ||
          sourceFileChanged ||
          serviceContextChanged
        ) {
          setTimeout(() => this.ensureRepaymentsLoaded(), 0)
        }
      })
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as Node | null
    if (!target) {
      return
    }

    ;(Object.keys(this.quickFilterPopovers) as QuickFilterKind[]).forEach(
      (kind) => {
        const popover = this.quickFilterPopovers[kind]
        if (!popover?.overlayVisible) {
          return
        }

        const overlay = popover.container
        const trigger = popover.target as HTMLElement | null
        const clickedInsideOverlay = !!overlay?.contains(target)
        const clickedTrigger = !!trigger?.contains(target)

        if (!clickedInsideOverlay && !clickedTrigger) {
          popover.hide()
        }
      },
    )
  }

  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild
    }
    return route
  }

  private applyPermissionsFromParams(params: Record<string, unknown>): void {
    const permissions = parseEmbedPermissions(params)
    this.canView = permissions.canView
    this.canEdit = permissions.canEdit
    this.canDelete = permissions.canDelete
  }

  private clearRepaymentsForNoView(): void {
    this.activeFilteredRepaymentsAbort?.abort()
    this.activeFilteredRepaymentsAbort = null
    this.loadingAllRepayments = false
    this.loading = false
    this.repayments = []
    this.repaymentsTotal = null
    this.sourceFile = null
  }

  private loadAllRepayments() {
    if (!this.canView) {
      this.clearRepaymentsForNoView()
      return
    }
    if (this.loadingAllRepayments) {
      return
    }

    // The table owns filtered requests. Fetching here as well races its
    // response and can replace matching rows with an empty parent input.
    if (this.hasActiveFilterQuery() && this.repaymentsTableComponent) {
      this.loading = false
      return
    }

    const loadId = this.startLoad()
    this.loadingAllRepayments = true
    this.repaymentsService.sourceFileId = null
    this.repayments = []
    this.repaymentsTotal = null
    if (this.hasActiveFilterQuery()) {
      this.loading = true
      this.fetchFilteredRepaymentsForView(loadId)
      return
    }
    this.loading = true
    this.fetchAllRepayments(loadId)
  }

  private fetchAllRepayments(loadId: number) {
    if (!this.canView) {
      this.clearRepaymentsForNoView()
      this.loadingAllRepayments = false
      return
    }
    if (this.destroyed) {
      this.loadingAllRepayments = false
      return
    }
    this.repaymentsService
      .loadAllRepayments({ signal: this.lifecycleAbort.signal })
      .then(() => {
        if (!this.isActiveLoad(loadId)) {
          this.loadingAllRepayments = false
          return
        }
        this.loading = false

        this.repayments = this.repaymentsService.repayments
        this.repaymentsTotal = null
      })
      .catch((error) => this.swallowAbort(error))
      .finally(() => {
        if (this.isActiveLoad(loadId)) {
          this.loadingAllRepayments = false
        }
      })
  }

  private loadRepaymentsFromFile(fileID: string) {
    if (!this.canView) {
      this.clearRepaymentsForNoView()
      return
    }
    if (this.destroyed) {
      return
    }
    const loadId = this.startLoad()

    this.repaymentsService.clearRepaymentsData()
    this.repaymentsService.sourceFileId = fileID
    this.repaymentsService.sourceFileIsCommitted = false
    this.tableQueryService.reset()
    this.tableQueryService.setPagination(0, 100)
    this.repayments = []
    this.repaymentsTotal = null
    this.sourceFileStatusBeforeEdits = null
    this.loading = true
    let pendingRequests = 2
    const finalizeLoading = () => {
      pendingRequests -= 1
      if (pendingRequests <= 0 && this.isActiveLoad(loadId)) {
        this.loading = false
      }
    }

    // Fetch the file record before selecting a data source. A PROCESSED file
    // must query repayments only; its staging rows are retained for audit.
    const sourceFilePromise = this.uploadedFileService.getFile(fileID, {
      signal: this.lifecycleAbort.signal,
      // A file commit is a durable job. Re-entering this route must not use a
      // pre-commit cache entry to decide whether progress should be shown.
      refresh: true,
    })
    sourceFilePromise.then((file) => {
      if (this.isActiveLoad(loadId)) {
        this.repaymentsService.sourceFileIsCommitted =
          this.isPersistedSourceFile(file?.status)
      }
    })

    this.loadCurrentFileRepaymentsPage(loadId, sourceFilePromise)
      .then(() => {
        if (!this.isActiveLoad(loadId)) {
          return
        }
      })
      .catch((error) => this.swallowAbort(error))
      .finally(() => {
        finalizeLoading()
      })

    this.loadCurrentFileRepaymentsCount(loadId, sourceFilePromise)
      .catch((error) => this.swallowAbort(error))
      .finally(() => {
        finalizeLoading()
      })

    sourceFilePromise
      .then((file) => {
        if (!this.isActiveLoad(loadId)) {
          return
        }
        this.sourceFile = file
        void this.refreshSourceFileCommitProgress()
        if (this.repaymentsTableComponent?.hasPendingChanges) {
          this.onPendingChangesChanged(true)
        }
      })
      .catch((error) => this.swallowAbort(error))
  }

  onPendingChangesChanged(hasPendingChanges: boolean): void {
    if (!this.sourceFile) {
      return
    }

    if (hasPendingChanges) {
      this.sourceFileStatusBeforeEdits ??= this.sourceFile.status
      this.sourceFile = { ...this.sourceFile, status: 'UNSAVED' }
      return
    }

    if (this.sourceFileStatusBeforeEdits !== null) {
      this.sourceFile = {
        ...this.sourceFile,
        status: this.sourceFileStatusBeforeEdits,
      }
      this.sourceFileStatusBeforeEdits = null
    }
  }

  private async loadCurrentFileRepaymentsPage(
    loadId: number,
    sourceFilePromise?: Promise<UploadedFile | null>,
  ): Promise<void> {
    if (!this.canView) {
      return
    }
    const sourceFile = sourceFilePromise ? await sourceFilePromise : this.sourceFile
    if (!this.isActiveLoad(loadId)) {
      return
    }
    this.applyDefaultDeletedFilter()
    const query = await this.tableQueryService.buildPostgrestParams(
      this.repaymentsService.buildBaseQueryParams(),
    )
    const repayments = await this.repaymentsService.fetchRepaymentsWithQuery(
      query,
      {
        signal: this.lifecycleAbort.signal,
        skipCacheUpdate: true,
        committedSourceFile:
          this.isPersistedSourceFile(sourceFile?.status),
      },
    )

    // The table owns a deleted-only refresh. Do not let an earlier active-file
    // request finish afterwards and replace its results with active rows.
    if (
      !this.isActiveLoad(loadId) ||
      this.repaymentsTableComponent?.showDeletedOnly
    ) {
      return
    }

    this.repayments = [...repayments]
  }

  private async loadCurrentFileRepaymentsCount(
    loadId: number,
    sourceFilePromise?: Promise<UploadedFile | null>,
  ): Promise<void> {
    if (!this.canView) {
      return
    }
    const sourceFile = sourceFilePromise ? await sourceFilePromise : this.sourceFile
    if (!this.isActiveLoad(loadId)) {
      return
    }
    this.applyDefaultDeletedFilter()
    const query = await this.tableQueryService.buildPostgrestParams(
      this.repaymentsService.buildBaseQueryParams(),
      { includePagination: false },
    )
    const count = await this.repaymentsService.fetchRepaymentsCountWithQuery(
      query,
      {
        signal: this.lifecycleAbort.signal,
        committedSourceFile:
          this.isPersistedSourceFile(sourceFile?.status),
      },
    )

    if (
      !this.isActiveLoad(loadId) ||
      this.repaymentsTableComponent?.showDeletedOnly
    ) {
      return
    }

    this.repaymentsTotal = count
  }

  private startLoad(): number {
    this.activeLoadId = ++this.loadCounter
    return this.activeLoadId
  }

  private isActiveLoad(loadId: number): boolean {
    return !this.destroyed && this.activeLoadId === loadId
  }

  private ensureRepaymentsLoaded(): void {
    if (!this.canView) {
      this.clearRepaymentsForNoView()
      return
    }
    const onAllRepaymentsTab =
      (this.currentPage === 'repayments' ||
        this.currentPage === 'in-progress') &&
      !this.repaymentsService.sourceFileId
    if (!onAllRepaymentsTab) {
      return
    }
    if (this.loadingAllRepayments) {
      return
    }
    this.loadAllRepayments()
  }

  public updateRepaymentCache(newlyLoadedRepayments: Repayments) {
    if (this.repayments.length === 0) {
      this.repayments = newlyLoadedRepayments
      return
    }
    // Get repayments already in cache

    // Loop through newly loaded data
    newlyLoadedRepayments.forEach((repayment) => {
      // Find existing record for each of the loaded repayments
      const staleRepayment = this.repayments.find((r) => r.id === repayment.id)

      if (staleRepayment) {
        // Update only the fields that changed
        Object.keys(repayment).forEach((key) => {
          staleRepayment[key] = repayment[key]
        })
      } else {
        // Add new repayment to the existing array (in-place)
        this.repayments.push(repayment)
      }
    })
  }

  clear(table: Table) {
    this.searchValue = ''
    if (this.sortField !== DEFAULT_SORT_FIELD) {
      this.sortField = DEFAULT_SORT_FIELD
    }

    if (this.sortOrder !== DEFAULT_SORT_ORDER) {
      this.sortOrder = DEFAULT_SORT_ORDER
    }

    Object.keys(table.filters).forEach((key) => {
      table.filters[key] = [
        { matchMode: 'startsWith', operator: 'and', value: null },
      ]
    })

    this.applyDeletedRepaymentsFilter(false, table, false)
    table._filter()
    this.sortField = DEFAULT_SORT_FIELD
    this.sortOrder = DEFAULT_SORT_ORDER
    this.selectedRepayments = []

    this.selectedMerchants = []
    this.selectedTerminals = []
    this.selectedCashAdvances = []
  }

  onFilter(event: any) {
    this.hasActiveFilters = Object.values(event.filters).some(
      (filterArray: any[]) =>
        Array.isArray(filterArray) &&
        filterArray.some(
          (f) =>
            f.value !== null &&
            f.value !== undefined &&
            f.value !== '' &&
            !(f.matchMode === 'equals' && f.value === false),
        ),
    )
  }

  onRowEditSave(repayment: any) {
    if (this.destroyed) {
      return
    }
    this.repaymentsService
      .updateRepayment(repayment, { signal: this.lifecycleAbort.signal })
      .then(() => {
        this.repaymentsService
          .loadAllRepayments({ signal: this.lifecycleAbort.signal })
          .catch((error) => this.swallowAbort(error))
      })
      .catch((error) => this.swallowAbort(error))
  }

  onRowEditInit(repayment: any) {}

  undoDelete(repayment: any) {
    if (this.destroyed) {
      return
    }
    const previousState = {
      deleted: !!repayment.deleted,
      staged: !!repayment.staged,
    }
    repayment.deleted = false
    repayment.staged = this.shouldStageRepaymentOnUndo(repayment)
    this.repaymentsService
      .undoDeleteRepayments([repayment], { signal: this.lifecycleAbort.signal })
      .then(() => {
        this.repaymentsService
          .loadAllRepayments({ signal: this.lifecycleAbort.signal })
          .catch((error) => this.swallowAbort(error))
      })
      .catch((error) => {
        repayment.deleted = previousState.deleted
        repayment.staged = previousState.staged
        this.swallowAbort(error)
      })
  }

  onTableSort(event: { field: string; order: number }) {
    console.log('Sorting by field', event.field, 'order', event.order)
    if (event.field === 'merchant') this.sortField = 'merchants.name'
    else if (event.field === 'terminal') this.sortField = 'terminals.name'
    else this.sortField = event.field
    this.sortOrder = event.order
  }

  deleteSelectedRepayments(event: any) {
    if (this.destroyed) {
      return
    }
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Delete ' + this.selectedRepayments.length + ' repayments?',
      header: 'Delete Repayments',
      icon: 'pi pi-info-circle',

      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger',
      },
      key: 'standardPopup',
      accept: () => {
        const previousStates = new Map<
          string,
          { deleted: boolean; staged: boolean }
        >()
        const repaymentsToDelete = this.selectedRepayments || []
        repaymentsToDelete.forEach((repayment) => {
          if (!repayment?.id) {
            return
          }
          previousStates.set(repayment.id, {
            deleted: !!repayment.deleted,
            staged: !!repayment.staged,
          })
          repayment.deleted = true
        })
        this.repaymentsService
          .deleteRepayments(repaymentsToDelete, {
            signal: this.lifecycleAbort.signal,
          })
          .then(() => {
            this.repaymentsService
              .loadAllRepayments({ signal: this.lifecycleAbort.signal })
              .catch((error) => this.swallowAbort(error))
            if (this.destroyed) {
              return
            }
            this.selectedRepayments = []
          })
          .catch((error) => {
            repaymentsToDelete.forEach((repayment) => {
              const previous = previousStates.get(repayment.id)
              if (previous) {
                repayment.deleted = previous.deleted
                repayment.staged = previous.staged
              }
            })
            this.swallowAbort(error)
          })
      },
      reject: () => {},
    })
  }

  undoDeleteSelectedRepayments(event: any) {
    if (this.destroyed) {
      return
    }
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message:
        'Undo delete on ' + this.selectedRepayments.length + ' repayments?',
      header: 'Undo Delete Repayments',
      icon: 'pi pi-info-circle',

      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Undo Delete',
        severity: 'info',
      },
      key: 'standardPopup',
      accept: () => {
        const previousStates = new Map<
          string,
          { deleted: boolean; staged: boolean }
        >()
        this.selectedRepayments.forEach((repayment) => {
          if (!repayment?.id) {
            return
          }
          previousStates.set(repayment.id, {
            deleted: !!repayment.deleted,
            staged: !!repayment.staged,
          })
          repayment.deleted = false
          repayment.staged = this.shouldStageRepaymentOnUndo(repayment)
        })
        this.repaymentsService
          .undoDeleteRepayments(this.selectedRepayments, {
            signal: this.lifecycleAbort.signal,
          })
          .then(() => {
            this.repaymentsService
              .loadAllRepayments({ signal: this.lifecycleAbort.signal })
              .catch((error) => this.swallowAbort(error))
            if (this.destroyed) {
              return
            }
            this.selectedRepayments = []
          })
          .catch((error) => {
            this.selectedRepayments.forEach((repayment) => {
              const previous = previousStates.get(repayment.id)
              if (previous) {
                repayment.deleted = previous.deleted
                repayment.staged = previous.staged
              }
            })
            this.swallowAbort(error)
          })
      },
      reject: () => {},
    })
  }

  private shouldStageRepaymentOnUndo(repayment: any): boolean {
    const sourceFileId = repayment?.source_file
    if (!sourceFileId) {
      return false
    }
    const file = this.uploadedFileService.files.find(
      (uploaded) => uploaded.id === sourceFileId,
    )
    if (!file?.status) {
      return true
    }
    return file.status.toUpperCase() === 'UNSAVED'
  }

  bulkEditRepayments(event: any) {
    if (this.destroyed) {
      return
    }
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Select a date to apply to all selected repayments:',
      icon: 'pi pi-exclamation-circle',

      rejectButtonProps: {
        icon: 'pi pi-times',
        label: 'Cancel',
        outlined: true,
      },
      acceptButtonProps: {
        icon: 'pi pi-check',
        label: 'Confirm',
      },
      key: 'customPopup',
      accept: () => {
        this.repaymentsService
          .bulkUpdateRepayments(
            this.selectedRepayments,
            {
              repayment_date: this.formatDateDdMmYyyy(this.popupDate),
              updated_by: this.authService.getAuditUser(),
            },
            { signal: this.lifecycleAbort.signal },
          )
          .then(() => {
            this.repaymentsService
              .loadAllRepayments({ signal: this.lifecycleAbort.signal })
              .catch((error) => this.swallowAbort(error))
          })
          .catch((error) => this.swallowAbort(error))
      },
      reject: () => {},
    })
  }

  toggleDeletedRepayments(table: Table) {
    if (this.destroyed) {
      return
    }
    this.applyDeletedRepaymentsFilter(!this.showDeletedOnly, table)
  }

  datePopupClicked(event: any) {
    event.preventDefault()
    event.stopPropagation()
  }

  get allSelectedDeleted(): boolean {
    return (
      this.selectedRepayments?.length > 0 &&
      this.selectedRepayments.every((r) => r.deleted)
    )
  }

  private applyDeletedRepaymentsFilter(
    showDeletedOnly: boolean,
    table: Table,
    applyTableFilter: boolean = true,
  ): void {
    this.showDeletedOnly = showDeletedOnly
    table.filters ??= {}
    table.filters['deleted'] = [
      this.createDeletedFilterConstraint(showDeletedOnly),
    ]
    if (applyTableFilter) {
      table._filter()
    }
  }

  private createDeletedFilterConstraint(
    showDeletedOnly: boolean,
  ): TableFilterConstraint {
    return {
      value: showDeletedOnly,
      matchMode: 'equals',
      operator: 'and',
    }
  }

  onFileClick() {
    this.router.navigate(['uploaded-files'], {
      queryParams: { sourceFileId: null },
      queryParamsHandling: 'merge', // keep existing query params
    })
  }

  async saveSourceFile(event: Event, file: UploadedFile): Promise<void> {
    event.preventDefault()
    event.stopPropagation()
    if (this.destroyed || !file.id || this.isSourceFileSaved(file)) return
    if (this.committingFileIds.has(file.id)) return
    this.committingFileIds.add(file.id)
    this.committingFileProgress.set(file.id, 0)
    try {
      const validation = await this.repaymentsService.validateStagedRepayments(
        file.id,
        {
          signal: this.lifecycleAbort.signal,
          requireMerchant: file.source !== FileSource.Custom,
        },
      )
      if (!validation.valid) {
        await this.uploadedFileService.updateFile(
          { ...file, status: 'ERROR' },
          { signal: this.lifecycleAbort.signal },
        )
        const visible = validation.errors.slice(0, 5).join('; ')
        this.messageService.add({
          severity: 'error',
          summary: 'Cannot commit file',
          detail: `Fix the required fields before committing. ${visible}${validation.errors.length > 5 ? `; and ${validation.errors.length - 5} more.` : ''}`,
          life: 8000,
        })
        return
      }
      await this.repaymentsService.stageRepayments(file.id, {
        signal: this.lifecycleAbort.signal,
        onQueued: () => {
          if (this.sourceFile?.id === file.id) {
            this.sourceFile = { ...this.sourceFile, status: 'COMMITTING' }
            this.uploadedFileService.updateUploadedFileCache([this.sourceFile])
          }
        },
        onProgress: (completed, total) => {
          this.committingFileProgress.set(
            file.id,
            total ? Math.floor((completed / total) * 100) : 0,
          )
        },
      })
      await this.uploadedFileService.updateFile(
        { ...file, status: 'PROCESSED' },
        { signal: this.lifecycleAbort.signal },
      )
      this.sourceFile = { ...file, status: 'PROCESSED' }
      this.messageService.add({
        severity: 'success',
        summary: 'File committed',
        detail: 'The file has been committed to Salesforce.',
        life: 4000,
      })
      this.loadRepaymentsFromFile(file.id)
    } catch (error) {
      this.swallowAbort(error)
    } finally {
      this.committingFileIds.delete(file.id)
      this.committingFileProgress.delete(file.id)
    }
  }

  isCommittingSourceFile(file: UploadedFile): boolean {
    return !!file.id &&
      (this.committingFileIds.has(file.id) ||
        file.status?.toUpperCase() === 'COMMITTING')
  }

  sourceFileCommitProgressPercent(file: UploadedFile): number {
    return file.id ? (this.committingFileProgress.get(file.id) ?? 0) : 0
  }

  private async refreshSourceFileCommitProgress(): Promise<void> {
    const file = this.sourceFile
    if (
      this.destroyed ||
      !file?.id ||
      file.status?.toUpperCase() !== 'COMMITTING'
    ) {
      return
    }
    try {
      const jobs = await this.repaymentsService.getActiveFileCommitJobs(
        [file.id],
        { signal: this.lifecycleAbort.signal },
      )
      const job = jobs[0]
      if (job) {
        this.committingFileProgress.set(
          file.id,
          job.total_rows > 0
            ? Math.floor((job.committed_rows / job.total_rows) * 100)
            : 0,
        )
        return
      }
      // The worker completed between polls. Reload the durable file status.
      const refreshed = await this.uploadedFileService.getFile(file.id, {
        signal: this.lifecycleAbort.signal,
        refresh: true,
      })
      if (!this.destroyed && refreshed) this.sourceFile = refreshed
    } catch (error) {
      this.swallowAbort(error)
    }
  }

  isSourceFileSaved(file: UploadedFile): boolean {
    return file.status?.toUpperCase() === 'PROCESSED'
  }

  private isPersistedSourceFile(status: string | null | undefined): boolean {
    const normalizedStatus = status?.toUpperCase()
    return normalizedStatus === 'SAVED' || normalizedStatus === 'PROCESSED'
  }

  hasPendingTableChanges(): boolean {
    return !!this.repaymentsTableComponent?.hasPendingChanges
  }

  toggleQuickFilter() {
    this.quickFilterVisible = !this.quickFilterVisible
  }

  getQuickFilterCount(kind: QuickFilterKind): number {
    return this.getSelectedQuickFilterOptions(kind).length
  }

  getQuickFilterIcon(kind: QuickFilterKind): string {
    switch (kind) {
      case 'merchant':
        return 'pi pi-shop'
      case 'terminal':
        return 'pi pi-credit-card'
      case 'cashAdvance':
        return 'pi pi-pound'
    }
  }

  getAppliedQuickFilterPills(): AppliedQuickFilterPill[] {
    const pills = [
      ...(this.selectedMerchants ?? []).map((item) => ({
        kind: 'merchant' as const,
        icon: this.getQuickFilterIcon('merchant'),
        label: item.name,
      })),
      ...(this.selectedTerminals ?? []).map((item) => ({
        kind: 'terminal' as const,
        icon: this.getQuickFilterIcon('terminal'),
        label: item.name,
      })),
      ...(this.selectedCashAdvances ?? []).map((item) => ({
        kind: 'cashAdvance' as const,
        icon: this.getQuickFilterIcon('cashAdvance'),
        label: item.name,
      })),
    ]
    const order = new Map(
      this.appliedQuickFilterOrder.map((key, index) => [key, index]),
    )
    return pills.sort((left, right) => {
      const leftIndex = order.get(
        this.getAppliedQuickFilterKey(left.kind, left.label),
      )
      const rightIndex = order.get(
        this.getAppliedQuickFilterKey(right.kind, right.label),
      )
      return (
        (leftIndex ?? Number.MAX_SAFE_INTEGER) -
        (rightIndex ?? Number.MAX_SAFE_INTEGER)
      )
    })
  }

  removeQuickFilterPill(
    kind: QuickFilterKind,
    label: string,
    event?: Event,
  ): void {
    event?.stopPropagation()
    switch (kind) {
      case 'merchant':
        this.selectedMerchants = this.selectedMerchants.filter(
          (item) => item.name !== label,
        )
        break
      case 'terminal':
        this.selectedTerminals = this.selectedTerminals.filter(
          (item) => item.name !== label,
        )
        break
      case 'cashAdvance':
        this.selectedCashAdvances = this.selectedCashAdvances.filter(
          (item) => item.name !== label,
        )
        break
    }
    this.reconcileQuickFilterSelections(kind)
    this.syncAppliedQuickFilterOrder()
    void this.applyQuickFiltersToTable().catch((error) => this.swallowAbort(error))
    void this.syncQuickFilterContextOptions()
      .catch((error) => this.swallowAbort(error))
      .finally(() => {
        if (this.destroyed) {
          return
        }
        this.refreshQuickFilterOptions('merchant')
        this.refreshQuickFilterOptions('terminal')
        this.refreshQuickFilterOptions('cashAdvance')
      })
    this.filterCriteriaChanged()
  }

  onAppliedQuickFilterHoverStart(
    kind: QuickFilterKind,
    label: string,
    event: MouseEvent,
  ): void {
    const target = event.currentTarget as HTMLElement | null
    if (!target) {
      return
    }
    this.hoveredAppliedQuickFilterKey = this.getAppliedQuickFilterKey(
      kind,
      label,
    )
    this.hoveredAppliedQuickFilterWidth = Math.ceil(
      target.getBoundingClientRect().width,
    )
  }

  onAppliedQuickFilterHoverEnd(kind: QuickFilterKind, label: string): void {
    const key = this.getAppliedQuickFilterKey(kind, label)
    if (this.hoveredAppliedQuickFilterKey !== key) {
      return
    }
    this.hoveredAppliedQuickFilterKey = null
    this.hoveredAppliedQuickFilterWidth = null
  }

  isAppliedQuickFilterHovered(kind: QuickFilterKind, label: string): boolean {
    return (
      this.hoveredAppliedQuickFilterKey ===
      this.getAppliedQuickFilterKey(kind, label)
    )
  }

  getAppliedQuickFilterHoverWidth(
    kind: QuickFilterKind,
    label: string,
  ): number | null {
    return this.isAppliedQuickFilterHovered(kind, label)
      ? this.hoveredAppliedQuickFilterWidth
      : null
  }

  private getAppliedQuickFilterKey(
    kind: QuickFilterKind,
    label: string,
  ): string {
    return `${kind}:${label}`
  }

  private async applyQuickFiltersToTable(): Promise<void> {
    const table = this.repaymentsTableComponent
    if (!table) {
      return
    }

    const merchantChanged = table.filterTable(
      'merchant_id',
      (this.selectedMerchants ?? []).map((item) => ({
        name: item.id ?? item.name,
      })),
      { refresh: false, emitQuickFilter: false },
    )
    const terminalChanged = table.filterTable(
      'terminal_id',
      (this.selectedTerminals ?? []).map((item) => ({
        name: item.id ?? item.name,
      })),
      { refresh: false, emitQuickFilter: false },
    )
    const cashAdvanceChanged = table.filterTable(
      'cash_advance_id',
      (this.selectedCashAdvances ?? []).map((item) => ({
        name: item.id ?? item.name,
      })),
      { refresh: false, emitQuickFilter: false },
    )

    if (merchantChanged || terminalChanged || cashAdvanceChanged) {
      await table.refreshQuickFilterData()
    }
  }

  private syncAppliedQuickFilterOrder(): void {
    const currentKeys = [
      ...(this.selectedMerchants ?? []).map((item) =>
        this.getAppliedQuickFilterKey('merchant', item.name),
      ),
      ...(this.selectedTerminals ?? []).map((item) =>
        this.getAppliedQuickFilterKey('terminal', item.name),
      ),
      ...(this.selectedCashAdvances ?? []).map((item) =>
        this.getAppliedQuickFilterKey('cashAdvance', item.name),
      ),
    ]
    const currentSet = new Set(currentKeys)
    this.appliedQuickFilterOrder = [
      ...this.appliedQuickFilterOrder.filter((key) => currentSet.has(key)),
      ...currentKeys.filter(
        (key) => !this.appliedQuickFilterOrder.includes(key),
      ),
    ]
  }

  filterCriteriaChanged() {
    if (this.destroyed) {
      return
    }
    const nextParams = { ...this.route.snapshot.queryParams }
    if (this.selectedMerchants?.length > 0) {
      nextParams['merchants(name)'] = this.selectedMerchants
        ? this.selectedMerchants.map((m) => m.name).join(',')
        : null
      const merchantIds = this.selectedMerchants
        .map((merchant) => merchant.id)
        .filter((id): id is string => !!id)
      if (merchantIds.length > 0) {
        nextParams['merchant_id'] = merchantIds.join(',')
      } else {
        delete nextParams['merchant_id']
      }
    } else {
      delete nextParams['merchants(name)']
      delete nextParams['merchant_id']
    }
    if (this.selectedTerminals?.length > 0) {
      nextParams['terminals(name)'] = this.selectedTerminals
        ? this.selectedTerminals.map((m) => m.name).join(',')
        : null
      const terminalIds = this.selectedTerminals
        .map((terminal) => terminal.id)
        .filter((id): id is string => !!id)
      if (terminalIds.length > 0) {
        nextParams['terminal_id'] = terminalIds.join(',')
      } else {
        delete nextParams['terminal_id']
      }
    } else {
      delete nextParams['terminals(name)']
      delete nextParams['terminal_id']
    }
    if (this.selectedCashAdvances?.length > 0) {
      nextParams['cash_advances(name)'] = this.selectedCashAdvances
        ? this.selectedCashAdvances.map((m) => m.name).join(',')
        : null
      const cashAdvanceIds = this.selectedCashAdvances
        .map((cashAdvance) => cashAdvance.id)
        .filter((id): id is string => !!id)
      if (cashAdvanceIds.length > 0) {
        nextParams['cash_advance_id'] = cashAdvanceIds.join(',')
      } else {
        delete nextParams['cash_advance_id']
      }
    } else {
      delete nextParams['cash_advances(name)']
      delete nextParams['cash_advance_id']
    }

    if (this.areQueryParamsEqual(this.route.snapshot.queryParams, nextParams)) {
      return
    }

    this.router.navigate([''], {
      relativeTo: this.route,
      queryParams: nextParams,
    })

    if (typeof window !== 'undefined' && (window as any).Cypress) {
      const params = new URLSearchParams()
      const selectedMerchants = this.selectedMerchants ?? []
      const selectedTerminals = this.selectedTerminals ?? []
      const selectedCashAdvances = this.selectedCashAdvances ?? []

      if (selectedMerchants.length > 0) {
        params.set(
          'merchants(name)',
          selectedMerchants.map((item) => item.name).join(','),
        )
      }
      if (selectedTerminals.length > 0) {
        params.set(
          'terminals(name)',
          selectedTerminals.map((item) => item.name).join(','),
        )
      }
      if (selectedCashAdvances.length > 0) {
        params.set(
          'cash_advances(name)',
          selectedCashAdvances.map((item) => item.name).join(','),
        )
      }

      fetch(`/repayments?${params.toString()}`, {
        method: 'GET',
        credentials: 'same-origin',
      }).catch(() => {
        // ignore test-only request failures
      })
    }

    // this.router.navigate([''], {
    //   relativeTo: this.route,
    //   queryParams: {
    //     merchants: this.selectedMerchants
    //       ? this.selectedMerchants.map((m) => m.name).join(',')
    //       : null,
    //     terminals: this.selectedTerminals
    //       ? this.selectedTerminals.map((m) => m.name).join(',')
    //       : null,
    //     cashAdvances: this.selectedCashAdvances
    //       ? this.selectedCashAdvances.map((m) => m.name).join(',')
    //       : null,
    //   },
    //   // queryParamsHandling: 'merge', // keep existing query params
    // })
    //   this.router.navigate([''], {
    //     relativeTo: this.route,
    //     queryParams: {
    //       merchants: this.selectedMerchants.map((m) => m.name),
    //     },
    //     queryParamsHandling: 'merge', // keep existing query params
    //   })
    // } else {
    //   this.router.navigate([], {
    //     relativeTo: this.route,
    //     queryParams: {
    //       merchants: null,
    //     },
    //     queryParamsHandling: 'merge', // keep existing query params
    //   })
    // }
    //   this.router.navigate([''], {
    //     queryParams: {
    //       terminals: this.selectedTerminals.map((m) => m.name),
    //     },
    //     queryParamsHandling: 'merge', // keep existing query params
    //   })
    // } else {
    //   this.router.navigate([''], {
    //     queryParams: {
    //       terminals: null,
    //     },
    //     queryParamsHandling: 'merge', // keep existing query params
    //   })
    // }
    //   this.router.navigate([''], {
    //     queryParams: {
    //       cashAdvances: this.selectedCashAdvances.map((m) => m.name),
    //     },
    //     queryParamsHandling: 'merge', // keep existing query params
    //   })
    // } else {
    //   this.router.navigate([''], {
    //     queryParams: {
    //       cashAdvances: null,
    //     },
    //     queryParamsHandling: 'merge', // keep existing query params
    //   })
    // }
  }

  private areQueryParamsEqual(
    current: Record<string, any>,
    next: Record<string, any>,
  ): boolean {
    const keys = new Set([...Object.keys(current || {}), ...Object.keys(next)])
    for (const key of keys) {
      const currentValue = current?.[key]
      const nextValue = next?.[key]
      if (currentValue !== nextValue) {
        return false
      }
    }
    return true
  }

  ngOnDestroy(): void {
    this.destroyed = true
    this.activeFilteredRepaymentsAbort?.abort()
    this.activeFilteredRepaymentsAbort = null
    this.resetQuickFilterSearch('merchant')
    this.resetQuickFilterSearch('terminal')
    this.resetQuickFilterSearch('cashAdvance')
    this.destroy$.next()
    this.destroy$.complete()
    this.closeAllPopovers()
    this.lifecycleAbort.abort()
    if (this.commitProgressPoll) clearInterval(this.commitProgressPoll)
    RepaymentsViewComponent.instanceCount--
    console.warn(
      `[RepaymentsViewComponent] destroyed instance ${this.instanceId} remaining=${RepaymentsViewComponent.instanceCount}`,
    )
  }

  onRepaymentsScrolledToBottom(): void {
    if (!this.canView) {
      return
    }
    console.log('Scrolled to bottom of repayments table')
  }

  private isAbortError(error: unknown): boolean {
    return (
      !!error &&
      typeof error === 'object' &&
      'name' in error &&
      (error as { name?: string }).name === 'AbortError'
    )
  }

  private swallowAbort(error: unknown) {
    if (this.isAbortError(error)) {
      return
    }

    this.messageService.add({
      severity: 'error',
      summary: 'Request failed',
      detail: error instanceof Error ? error.message : 'Please try again.',
      life: 7000,
    })
  }

  private beginFilteredRepaymentsRequest(): AbortSignal {
    this.activeFilteredRepaymentsAbort?.abort()
    const controller = new AbortController()
    this.activeFilteredRepaymentsAbort = controller
    return controller.signal
  }

  private closeAllPopovers(): void {
    this.popovers?.forEach((popover) => {
      try {
        // TODO
      } catch {
        // ignore
      }
    })

    // hide()
    // Fallback to close any overlay left behind
    setTimeout(() => {
      // hide()
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))
    }, 0)
  }

  private resetTableState(): void {
    this.repaymentsService.clearRepaymentsData()
    this.tableQueryService.reset()
    this.tableQueryService.setPagination(0, 100)
    this.applyDefaultDeletedFilter()
    this.repayments = []
    this.repaymentsTotal = null
    this.loading = true
  }

  private applyDefaultDeletedFilter(): void {
    this.showDeletedOnly = false
    this.tableQueryService.setFilter('deleted', {
      value: false,
      matchMode: 'equals',
      operator: 'and',
    })
  }

  private prepareRepaymentsLoadingState(): void {
    this.loadingAllRepayments = false
    this.repayments = []
    this.repaymentsTotal = null
    this.loading = true
  }

  private shouldResetForRoutePageChange(
    previousPage: string | null | undefined,
    nextPage: string | null | undefined,
  ): boolean {
    const previous = previousPage ?? ''
    const next = nextPage ?? ''
    if (!previous || !next || previous === next) {
      return false
    }

    const repaymentsPages = new Set(['repayments', 'in-progress'])
    return repaymentsPages.has(previous) && repaymentsPages.has(next)
  }

  private getRoutePageFromUrl(url: string): string {
    const tree = this.router.parseUrl(url)
    const primarySegments = tree.root.children['primary']?.segments ?? []
    return primarySegments[primarySegments.length - 1]?.path ?? ''
  }

  private isRoutePathChanging(nextUrl: string): boolean {
    const currentTree = this.router.parseUrl(this.router.url)
    const nextTree = this.router.parseUrl(nextUrl)
    const currentPath =
      currentTree.root.children['primary']?.segments
        ?.map((segment) => segment.path)
        .join('/') ?? ''
    const nextPath =
      nextTree.root.children['primary']?.segments
        ?.map((segment) => segment.path)
        .join('/') ?? ''
    return currentPath !== nextPath
  }

  private formatDateDdMmYyyy(date: Date | undefined): string {
    if (!date) {
      return ''
    }
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  private hasActiveFilterQuery(): boolean {
    const merchantFilters = Array.isArray(this.selectedMerchants)
      ? this.selectedMerchants.length
      : 0
    const terminalFilters = Array.isArray(this.selectedTerminals)
      ? this.selectedTerminals.length
      : 0
    const cashAdvanceFilters = Array.isArray(this.selectedCashAdvances)
      ? this.selectedCashAdvances.length
      : 0

    return (
      merchantFilters > 0 ||
      terminalFilters > 0 ||
      cashAdvanceFilters > 0 ||
      this.tableQueryService.hasActiveFilters() ||
      this.tableQueryService.hasGlobalFilter()
    )
  }

  private async fetchFilteredRepaymentsForView(loadId: number): Promise<void> {
    if (!this.canView) {
      this.clearRepaymentsForNoView()
      return
    }
    if (!this.isActiveLoad(loadId)) {
      this.loadingAllRepayments = false
      this.loading = false
      return
    }

    const requestSignal = this.beginFilteredRepaymentsRequest()

    let query: string
    const hasQuickFilters = this.hasActiveQuickFilters()
    try {
      query = await this.tableQueryService.buildPostgrestParams(
        this.repaymentsService.buildBaseQueryParams(),
        { includePagination: !hasQuickFilters },
      )
    } catch (error) {
      console.error('Failed to build filtered repayments query', error)
      this.loadingAllRepayments = false
      this.loading = false
      if (this.activeFilteredRepaymentsAbort?.signal === requestSignal) {
        this.activeFilteredRepaymentsAbort = null
      }
      return
    }

    if (!query) {
      this.loadingAllRepayments = false
      this.loading = false
      if (this.activeFilteredRepaymentsAbort?.signal === requestSignal) {
        this.activeFilteredRepaymentsAbort = null
      }
      return
    }

    try {
      const count = await this.repaymentsService.fetchRepaymentsCountWithQuery(
        query,
        {
          signal: requestSignal,
        },
      )
      if (!this.isActiveLoad(loadId)) {
        return
      }
      const repaymentsQuery =
        hasQuickFilters && typeof count === 'number'
          ? this.withFullResultPagination(query, count)
          : query
      const repayments = await this.repaymentsService.fetchRepaymentsWithQuery(
        repaymentsQuery,
        {
          signal: requestSignal,
        },
      )
      if (!this.isActiveLoad(loadId)) {
        return
      }
      this.repaymentsTotal = typeof count === 'number' ? count : null
      this.repayments = repayments
    } catch (error) {
      this.swallowAbort(error)
    } finally {
      if (this.isActiveLoad(loadId)) {
        this.loadingAllRepayments = false
        this.loading = false
        if (this.activeFilteredRepaymentsAbort?.signal === requestSignal) {
          this.activeFilteredRepaymentsAbort = null
        }
      }
    }
  }

  onQuickFilterPopoverHide(kind: QuickFilterKind): void {
    delete this.quickFilterPopovers[kind]
    this.resetQuickFilterSearch(kind)
    if (!this.quickFilterRouteSyncPending[kind]) {
      return
    }

    this.quickFilterRouteSyncPending[kind] = false
    this.filterCriteriaChanged()
  }

  toggleQuickFilterPopover(
    kind: QuickFilterKind,
    popover: Popover,
    event: Event,
  ): void {
    popover.toggle(event)
    const state = this.getQuickFilterSearchState(kind)
    if (!state.term.trim()) {
      if (this.shouldUseContextScopedQuickFilterOptions(kind)) {
        state.loading = true
        state.error = null
        state.results = []
        state.options = [...this.getSelectedQuickFilterOptions(kind)]
      }
      this.loadInitialQuickFilterOptions(kind)
    }
  }

  onQuickFilterSearchInput(kind: QuickFilterKind, value: string): void {
    const state = this.getQuickFilterSearchState(kind)
    state.term = value
    state.error = null

    if (state.debounceHandle) {
      clearTimeout(state.debounceHandle)
      state.debounceHandle = null
    }

    const normalized = value.trim()
    if (!normalized) {
      state.loading = false
      state.loadingMore = false
      state.searched = false
      state.results = []
      this.refreshQuickFilterOptions(kind)
      return
    }

    state.loading = true
    state.searched = true
    state.debounceHandle = setTimeout(() => {
      state.debounceHandle = null
      this.runQuickFilterSearch(kind, normalized)
    }, 250)
  }

  async onQuickFilterSelectionChange(kind?: QuickFilterKind): Promise<void> {
    try {
      await this.ensureQuickFilterRelationsLoaded(kind)
      this.reconcileQuickFilterSelections(kind)
      await this.syncQuickFilterContextOptions()
      this.syncAppliedQuickFilterOrder()
      this.refreshQuickFilterOptions('merchant')
      this.refreshQuickFilterOptions('terminal')
      this.refreshQuickFilterOptions('cashAdvance')
      await this.applyQuickFiltersToTable()
    } catch (error) {
      this.swallowAbort(error)
      return
    }

    if (kind && this.quickFilterPopovers[kind]?.overlayVisible) {
      this.quickFilterRouteSyncPending[kind] = true
      return
    }

    if (kind) {
      this.quickFilterRouteSyncPending[kind] = false
    }
    this.filterCriteriaChanged()
  }

  onMerchantQuickFilterModelChange(
    selection: QuickFilterOption | QuickFilterOption[] | null,
  ): void {
    this.selectedMerchants = selection
      ? [Array.isArray(selection) ? selection[0] : selection]
      : []
    void this.onQuickFilterSelectionChange('merchant')
  }

  getQuickFilterOptions(kind: QuickFilterKind): QuickFilterOption[] {
    return this.getQuickFilterSearchState(kind).options
  }

  getQuickFilterState(kind: QuickFilterKind): QuickFilterSearchState {
    return this.getQuickFilterSearchState(kind)
  }

  getQuickFilterEmptyMessage(kind: QuickFilterKind): string {
    const state = this.getQuickFilterSearchState(kind)
    if (state.error) {
      return state.error
    }
    if (
      state.loading ||
      (this.shouldUseContextScopedQuickFilterOptions(kind) &&
        this.quickFilterContextLoading)
    ) {
      return 'Searching...'
    }
    return 'No matches found'
  }

  isQuickFilterContextPending(kind: QuickFilterKind): boolean {
    const state = this.getQuickFilterSearchState(kind)
    return (
      this.shouldUseContextScopedQuickFilterOptions(kind) &&
      this.quickFilterContextLoading &&
      !this.quickFilterContextLoaded &&
      !state.term.trim()
    )
  }

  private createQuickFilterSearchState(): QuickFilterSearchState {
    return {
      term: '',
      loading: false,
      loadingMore: false,
      searched: false,
      results: [],
      options: [],
      error: null,
      debounceHandle: null,
      scrollCleanup: null,
    }
  }

  private getQuickFilterSearchState(
    kind: QuickFilterKind,
  ): QuickFilterSearchState {
    switch (kind) {
      case 'merchant':
        return this.merchantSearch
      case 'terminal':
        return this.terminalSearch
      case 'cashAdvance':
        return this.cashAdvanceSearch
    }
  }

  private getSelectedQuickFilterOptions(
    kind: QuickFilterKind,
  ): QuickFilterOption[] {
    switch (kind) {
      case 'merchant':
        return this.selectedMerchants ?? []
      case 'terminal':
        return this.selectedTerminals ?? []
      case 'cashAdvance':
        return this.selectedCashAdvances ?? []
    }
  }

  private getCachedQuickFilterOptions(
    kind: QuickFilterKind,
  ): QuickFilterOption[] {
    switch (kind) {
      case 'merchant':
        return this.normalizeQuickFilterOptions(
          this.repaymentsService.merchants,
        )
      case 'terminal':
        return this.normalizeQuickFilterOptions(
          this.repaymentsService.terminals,
        )
      case 'cashAdvance':
        return this.normalizeQuickFilterOptions(
          this.repaymentsService.cashAdvances,
        )
    }
  }

  private shouldUseContextScopedQuickFilterOptions(
    kind: QuickFilterKind,
  ): boolean {
    if (kind === 'merchant') {
      return false
    }

    const state = this.getQuickFilterSearchState(kind)
    if (state.term.trim()) {
      return false
    }

    const context = this.getQuickFilterMerchantContext()
    return !!context.id || !!context.name
  }

  hasMoreQuickFilterOptions(kind: QuickFilterKind): boolean {
    if (this.shouldUseContextScopedQuickFilterOptions(kind)) {
      return false
    }

    switch (kind) {
      case 'merchant':
        return this.repaymentsService.hasMoreMerchants()
      case 'terminal':
        return this.repaymentsService.hasMoreTerminals()
      case 'cashAdvance':
        return this.repaymentsService.hasMoreCashAdvances()
    }
  }

  onQuickFilterListScroll(kind: QuickFilterKind, event: Event): void {
    const state = this.getQuickFilterSearchState(kind)
    if (state.term.trim() || state.loading || state.loadingMore) {
      return
    }

    const target = event.target as HTMLElement | null
    if (!target) {
      return
    }

    const threshold = 24
    const atBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - threshold

    if (atBottom) {
      void this.loadMoreQuickFilterOptions(kind)
    }
  }

  private async runQuickFilterSearch(
    kind: QuickFilterKind,
    normalizedTerm: string,
  ): Promise<void> {
    if (this.destroyed) {
      return
    }

    const state = this.getQuickFilterSearchState(kind)
    state.loading = true
    state.error = null

    try {
      let results: QuickFilterOption[]
      switch (kind) {
        case 'merchant':
          results = await this.repaymentsService.searchMerchantsByName(
            normalizedTerm,
            { signal: this.lifecycleAbort.signal },
          )
          break
        case 'terminal':
          results = await this.repaymentsService.searchTerminalsByName(
            normalizedTerm,
            { signal: this.lifecycleAbort.signal },
          )
          break
        case 'cashAdvance':
          results = await this.repaymentsService.searchCashAdvancesByName(
            normalizedTerm,
            { signal: this.lifecycleAbort.signal },
          )
          break
      }

      if (state.term.trim() !== normalizedTerm) {
        return
      }

      state.results = results
      this.refreshQuickFilterOptions(kind)
    } catch (error) {
      if (this.isAbortError(error)) {
        return
      }
      if (state.term.trim() !== normalizedTerm) {
        return
      }
      state.results = []
      state.error = 'Search failed. Try again.'
      this.refreshQuickFilterOptions(kind)
    } finally {
      if (state.term.trim() === normalizedTerm) {
        state.loading = false
      }
    }
  }

  private async loadInitialQuickFilterOptions(
    kind: QuickFilterKind,
  ): Promise<void> {
    const state = this.getQuickFilterSearchState(kind)
    state.loading = true
    state.error = null

    const contextOptions = await this.getContextDrivenQuickFilterOptions(kind)
    if (contextOptions.length > 0) {
      state.results = contextOptions
      this.refreshQuickFilterOptions(kind)
      this.rebindQuickFilterScrollListenerIfVisible(kind)
      state.loading = false
      return
    }

    const cached = this.getCachedQuickFilterOptions(kind)
    if (cached.length > 0) {
      state.results = cached
      this.refreshQuickFilterOptions(kind)
      state.loading = false
      return
    }

    try {
      switch (kind) {
        case 'merchant':
          await this.repaymentsService.loadAllMerchants({
            signal: this.lifecycleAbort.signal,
          })
          break
        case 'terminal':
          await this.repaymentsService.loadAllTerminals({
            signal: this.lifecycleAbort.signal,
          })
          break
        case 'cashAdvance':
          await this.repaymentsService.loadAllCashAdvances({
            signal: this.lifecycleAbort.signal,
          })
          break
      }

      if (this.destroyed || state.term.trim()) {
        return
      }

      state.results = this.getCachedQuickFilterOptions(kind)
      this.refreshQuickFilterOptions(kind)
      this.rebindQuickFilterScrollListenerIfVisible(kind)
    } catch (error) {
      if (this.isAbortError(error)) {
        return
      }
      if (this.destroyed || state.term.trim()) {
        return
      }
      state.error = 'Failed to load options'
      state.results = []
      this.refreshQuickFilterOptions(kind)
    } finally {
      if (!state.term.trim()) {
        state.loading = false
      }
    }
  }

  onQuickFilterPopoverShow(kind: QuickFilterKind, popover: Popover): void {
    this.quickFilterPopovers[kind] = popover
    this.bindQuickFilterScrollListener(kind, popover)
  }

  private async loadMoreQuickFilterOptions(
    kind: QuickFilterKind,
  ): Promise<void> {
    const state = this.getQuickFilterSearchState(kind)
    if (
      state.term.trim() ||
      state.loading ||
      state.loadingMore ||
      !this.hasMoreQuickFilterOptions(kind)
    ) {
      return
    }

    state.loadingMore = true
    state.error = null

    try {
      switch (kind) {
        case 'merchant':
          await this.repaymentsService.loadMoreMerchants({
            signal: this.lifecycleAbort.signal,
          })
          break
        case 'terminal':
          await this.repaymentsService.loadMoreTerminals({
            signal: this.lifecycleAbort.signal,
          })
          break
        case 'cashAdvance':
          await this.repaymentsService.loadMoreCashAdvances({
            signal: this.lifecycleAbort.signal,
          })
          break
      }

      if (this.destroyed || state.term.trim()) {
        return
      }

      state.results = this.getCachedQuickFilterOptions(kind)
      this.refreshQuickFilterOptions(kind)
    } catch (error) {
      if (this.isAbortError(error)) {
        return
      }
      if (this.destroyed || state.term.trim()) {
        return
      }
      state.error = 'Failed to load more options'
    } finally {
      state.loadingMore = false
    }
  }

  private resetQuickFilterSearch(kind: QuickFilterKind): void {
    const state = this.getQuickFilterSearchState(kind)
    if (state.debounceHandle) {
      clearTimeout(state.debounceHandle)
      state.debounceHandle = null
    }
    if (state.scrollCleanup) {
      state.scrollCleanup()
      state.scrollCleanup = null
    }
    state.term = ''
    state.loading = false
    state.loadingMore = false
    state.searched = false
    state.results = []
    state.options = this.mergeDisplayQuickFilterOptions(
      kind,
      this.shouldUseContextScopedQuickFilterOptions(kind)
        ? []
        : this.getCachedQuickFilterOptions(kind),
      this.getSelectedQuickFilterOptions(kind),
    )
    state.error = null
  }

  private mergeDisplayQuickFilterOptions(
    kind: QuickFilterKind,
    available: QuickFilterOption[],
    selected: QuickFilterOption[],
  ): QuickFilterOption[] {
    if (kind === 'merchant') {
      return this.mergeQuickFilterOptions(selected, available)
    }

    return this.mergeQuickFilterOptions(available, selected)
  }

  private refreshQuickFilterOptions(kind: QuickFilterKind): void {
    const state = this.getQuickFilterSearchState(kind)
    const selected = this.getSelectedQuickFilterOptions(kind)
    if (
      this.shouldUseContextScopedQuickFilterOptions(kind) &&
      this.quickFilterContextLoading &&
      !this.quickFilterContextLoaded &&
      !state.term.trim()
    ) {
      state.options = [...selected]
      return
    }

    const shouldUseContextResults =
      this.shouldUseContextScopedQuickFilterOptions(kind) &&
      state.results.length > 0
    const available =
      state.term.trim() || shouldUseContextResults
        ? state.results
        : this.getCachedQuickFilterOptions(kind)
    state.options = this.mergeDisplayQuickFilterOptions(
      kind,
      this.filterQuickFilterOptionsForContext(kind, available),
      selected,
    )
  }

  private normalizeQuickFilterOptions(
    items:
      | Array<{
          id?: string
          name?: string
          merchant_id?: string
          merchantId?: string
          cash_advance_id?: string
          cashAdvanceId?: string
          active_cash_advance?: boolean | null
          activeCashAdvance?: boolean
          cash_advances?:
            | { merchant_id?: string }
            | Array<{ merchant_id?: string }>
        }>
      | undefined,
  ): QuickFilterOption[] {
    const normalized: QuickFilterOption[] = []
    const seen = new Set<string>()

    for (const item of items ?? []) {
      const name = typeof item?.name === 'string' ? item.name.trim() : ''
      if (!name) {
        continue
      }

      const id = item?.id ? String(item.id) : undefined
      const merchantId =
        item?.merchantId ||
        item?.merchant_id ||
        (Array.isArray(item?.cash_advances)
          ? item.cash_advances[0]?.merchant_id
          : item?.cash_advances?.merchant_id)
      const cashAdvanceId = item?.cashAdvanceId || item?.cash_advance_id
      const activeCashAdvance =
        typeof item?.activeCashAdvance === 'boolean'
          ? item.activeCashAdvance
          : typeof item?.active_cash_advance === 'boolean'
            ? item.active_cash_advance
            : undefined
      const key = name.toLocaleLowerCase()
      if (seen.has(key)) {
        continue
      }

      seen.add(key)
      normalized.push({
        id,
        name,
        merchantId: merchantId ? String(merchantId) : undefined,
        cashAdvanceId: cashAdvanceId ? String(cashAdvanceId) : undefined,
        activeCashAdvance,
      })
    }

    return normalized
  }

  private mergeQuickFilterOptions(
    primary: QuickFilterOption[],
    secondary: QuickFilterOption[],
  ): QuickFilterOption[] {
    const merged: QuickFilterOption[] = []
    const seen = new Set<string>()

    ;[...(primary ?? []), ...(secondary ?? [])].forEach((item) => {
      const name = typeof item?.name === 'string' ? item.name.trim() : ''
      if (!name) {
        return
      }

      const id = item?.id ? String(item.id) : undefined
      const key = name.toLocaleLowerCase()
      if (seen.has(key)) {
        return
      }

      seen.add(key)
      merged.push({
        id,
        name,
        merchantId: item?.merchantId,
        cashAdvanceId: item?.cashAdvanceId,
      })
    })

    return merged
  }

  private parseQuickFilterParams(
    kind: QuickFilterKind,
    value: string | string[],
  ): QuickFilterOption[] {
    const raw = Array.isArray(value) ? value.join(',') : value
    return raw
      .split(',')
      .map((name) =>
        this.resolveQuickFilterOption(kind, { name: String(name).trim() }),
      )
      .filter((item): item is QuickFilterOption => !!item?.name)
  }

  private reconcileQuickFilterSelections(kind?: QuickFilterKind): void {
    this.selectedMerchants = this.resolveQuickFilterSelectionArray(
      'merchant',
      this.selectedMerchants,
    )
    this.selectedTerminals = this.resolveQuickFilterSelectionArray(
      'terminal',
      this.selectedTerminals,
    )
    this.selectedCashAdvances = this.resolveQuickFilterSelectionArray(
      'cashAdvance',
      this.selectedCashAdvances,
    )

    const contextMerchantId = this.getQuickFilterContextMerchantId(kind)

    if (contextMerchantId) {
      const merchantOption = this.findMerchantOptionById(contextMerchantId)
      this.selectedMerchants = merchantOption
        ? [merchantOption]
        : this.selectedMerchants.slice(0, 1)
      this.selectedTerminals = this.selectedTerminals.filter(
        (item) =>
          this.getOptionMerchantId('terminal', item) === contextMerchantId,
      )
      this.selectedCashAdvances = this.selectedCashAdvances.filter(
        (item) =>
          this.getOptionMerchantId('cashAdvance', item) === contextMerchantId,
      )
      return
    }

    if (kind === 'merchant' && this.selectedMerchants.length > 1) {
      this.selectedMerchants = this.selectedMerchants.slice(-1)
    }
  }

  private getQuickFilterContextMerchantId(
    kind?: QuickFilterKind,
  ): string | undefined {
    if (kind) {
      const lastSelected = this.getLastSelectedQuickFilterOption(kind)
      const kindMerchantId = this.getOptionMerchantId(kind, lastSelected)
      if (kindMerchantId) {
        return kindMerchantId
      }
    }

    return (
      this.getOptionMerchantId('merchant', this.selectedMerchants[0]) ||
      this.getOptionMerchantId('terminal', this.selectedTerminals[0]) ||
      this.getOptionMerchantId('cashAdvance', this.selectedCashAdvances[0])
    )
  }

  private getLastSelectedQuickFilterOption(
    kind: QuickFilterKind,
  ): QuickFilterOption | undefined {
    const selected = this.getSelectedQuickFilterOptions(kind)
    return selected.length > 0 ? selected[selected.length - 1] : undefined
  }

  private filterQuickFilterOptionsForContext(
    kind: QuickFilterKind,
    options: QuickFilterOption[],
  ): QuickFilterOption[] {
    if (kind === 'merchant') {
      return options
    }

    const context = this.getQuickFilterMerchantContext()
    if (!context.id && !context.name) {
      return options
    }

    if (
      this.shouldUseContextScopedQuickFilterOptions(kind) &&
      this.quickFilterContextLoading &&
      !this.quickFilterContextLoaded
    ) {
      return []
    }

    const merchantScopedOptions = this.mergeQuickFilterOptions(
      this.getQuickFilterContextOptions(kind),
      this.getQuickFilterOptionsFromRepayments(kind, context),
    )

    if (!context.id) {
      return merchantScopedOptions.length > 0
        ? this.mergeQuickFilterOptions(merchantScopedOptions, options)
        : options
    }

    const filteredOptions = options.filter((item) =>
      this.optionBelongsToMerchantContext(kind, item, context.id!),
    )
    return merchantScopedOptions.length > 0
      ? this.mergeQuickFilterOptions(merchantScopedOptions, filteredOptions)
      : filteredOptions
  }

  private resolveQuickFilterSelectionArray(
    kind: QuickFilterKind,
    selected: QuickFilterOption[],
  ): QuickFilterOption[] {
    return selected
      .map((item) => this.resolveQuickFilterOption(kind, item))
      .filter((item): item is QuickFilterOption => !!item?.name)
  }

  private resolveQuickFilterOption(
    kind: QuickFilterKind,
    option: QuickFilterOption | undefined,
  ): QuickFilterOption | undefined {
    if (!option) {
      return undefined
    }

    const key = option.name.trim().toLocaleLowerCase()
    const id = option.id ? String(option.id) : undefined
    const candidates = [
      option,
      ...this.getSelectedQuickFilterOptions(kind),
      ...this.getQuickFilterSearchState(kind).results,
      ...this.getCachedQuickFilterOptions(kind),
    ]

    const resolved = candidates.find((candidate) => {
      if (!candidate) {
        return false
      }
      if (id && candidate.id) {
        return String(candidate.id) === id
      }
      return candidate.name.trim().toLocaleLowerCase() === key
    })

    if (!resolved) {
      return option
    }

    return {
      id: resolved.id ?? option.id,
      name: resolved.name ?? option.name,
      merchantId: resolved.merchantId ?? option.merchantId,
      cashAdvanceId: resolved.cashAdvanceId ?? option.cashAdvanceId,
    }
  }

  private getOptionMerchantId(
    kind: QuickFilterKind,
    option: QuickFilterOption | undefined,
  ): string | undefined {
    const resolved = this.resolveQuickFilterOption(kind, option)
    if (!resolved) {
      return undefined
    }

    switch (kind) {
      case 'merchant':
        return resolved.id
      case 'terminal':
        return (
          resolved.merchantId ??
          this.getMerchantIdFromCashAdvanceId(resolved.cashAdvanceId) ??
          this.getMerchantIdForRelation('terminal', resolved)
        )
      case 'cashAdvance':
        return (
          resolved.merchantId ??
          this.getMerchantIdForRelation('cashAdvance', resolved)
        )
    }
  }

  private findMerchantOptionById(
    merchantId: string,
  ): QuickFilterOption | undefined {
    const merchant = this.getCachedQuickFilterOptions('merchant').find(
      (item) => item.id === merchantId,
    )
    if (merchant) {
      return merchant
    }

    return (
      this.merchantSearch.results.find((item) => item.id === merchantId) ??
      this.getQuickFilterRelationRepayments().find(
        (repayment) => repayment.merchant_id === merchantId,
      )?.merchants
    )
  }

  private async ensureQuickFilterRelationsLoaded(
    kind?: QuickFilterKind,
  ): Promise<void> {
    if (kind === 'terminal') {
      if (!this.repaymentsService.areCashAdvancesCached()) {
        await this.repaymentsService.loadAllCashAdvances({
          signal: this.lifecycleAbort.signal,
        })
      }
      if (!this.repaymentsService.areMerchantsCached()) {
        await this.repaymentsService.loadAllMerchants({
          signal: this.lifecycleAbort.signal,
        })
      }
      return
    }

    if (
      kind === 'cashAdvance' &&
      !this.repaymentsService.areMerchantsCached()
    ) {
      await this.repaymentsService.loadAllMerchants({
        signal: this.lifecycleAbort.signal,
      })
    }
  }

  private optionBelongsToMerchantContext(
    kind: QuickFilterKind,
    option: QuickFilterOption,
    merchantId: string,
  ): boolean {
    const optionMerchantId = this.getOptionMerchantId(kind, option)
    if (optionMerchantId) {
      return optionMerchantId === merchantId
    }

    if (kind === 'terminal' || kind === 'cashAdvance') {
      return this.getMerchantIdForRelation(kind, option) === merchantId
    }

    return false
  }

  private getMerchantIdForRelation(
    kind: 'terminal' | 'cashAdvance',
    option: QuickFilterOption | undefined,
  ): string | undefined {
    if (!option) {
      return undefined
    }

    const repayments = this.getQuickFilterRelationRepayments()
    const optionId = option.id ? String(option.id) : undefined
    const optionName = option.name.trim().toLocaleLowerCase()

    for (const repayment of repayments) {
      const relationId =
        kind === 'terminal' ? repayment.terminal_id : repayment.cash_advance_id
      const relationName =
        kind === 'terminal'
          ? repayment.terminals?.name
          : repayment.cash_advances?.name

      const matchesId =
        !!optionId && !!relationId && String(relationId) === optionId
      const matchesName =
        !optionId &&
        typeof relationName === 'string' &&
        relationName.trim().toLocaleLowerCase() === optionName

      if (matchesId || matchesName) {
        return repayment.merchant_id ? String(repayment.merchant_id) : undefined
      }
    }

    return undefined
  }

  private getQuickFilterRelationRepayments() {
    const merged: Repayments = []
    const seen = new Set<string>()

    ;[
      ...(this.quickFilterContextRepayments ?? []),
      ...(this.repayments ?? []),
      ...(this.repaymentsService.repayments ?? []),
    ].forEach((repayment) => {
      const id = repayment?.id ? String(repayment.id) : ''
      const key =
        id ||
        [
          repayment?.merchant_id ?? '',
          repayment?.cash_advance_id ?? '',
          repayment?.terminal_id ?? '',
          repayment?.repayment_date ?? '',
          repayment?.payment_amount ?? '',
        ].join(':')

      if (!key || seen.has(key)) {
        return
      }

      seen.add(key)
      merged.push(repayment)
    })

    return merged
  }

  private hasActiveQuickFilters(): boolean {
    return (
      (this.selectedMerchants?.length ?? 0) > 0 ||
      (this.selectedTerminals?.length ?? 0) > 0 ||
      (this.selectedCashAdvances?.length ?? 0) > 0
    )
  }

  private withFullResultPagination(queryString: string, count: number): string {
    const params = new URLSearchParams(queryString)
    params.set('limit', String(Math.max(count, 1)))
    params.set('offset', '0')
    return params.toString()
  }

  private async getContextDrivenQuickFilterOptions(
    kind: QuickFilterKind,
  ): Promise<QuickFilterOption[]> {
    if (kind === 'merchant') {
      return []
    }

    await this.syncQuickFilterContextOptions()
    const context = this.getQuickFilterMerchantContext()
    if (!context.id && !context.name) {
      return []
    }

    return this.mergeQuickFilterOptions(
      this.getQuickFilterContextOptions(kind),
      this.getQuickFilterOptionsFromRepayments(kind, context),
    )
  }

  private async syncQuickFilterContextOptions(): Promise<void> {
    if (!this.canView) {
      this.quickFilterContextRepayments = []
      this.quickFilterContextTerminalOptions = []
      this.quickFilterContextCashAdvanceOptions = []
      this.quickFilterContextKey = null
      this.quickFilterContextLoaded = false
      this.quickFilterContextLoading = false
      return
    }

    const context = this.getQuickFilterMerchantContext()
    const merchantIds = await this.getQuickFilterContextMerchantIds(context)
    const contextKey =
      merchantIds.length > 0
        ? merchantIds.join(',')
        : (context.name?.trim().toLocaleLowerCase() ?? null)

    if (!contextKey) {
      this.quickFilterContextRepayments = []
      this.quickFilterContextTerminalOptions = []
      this.quickFilterContextCashAdvanceOptions = []
      this.quickFilterContextKey = null
      this.quickFilterContextLoaded = false
      return
    }

    if (
      this.quickFilterContextKey === contextKey &&
      this.quickFilterContextLoaded
    ) {
      return
    }

    const loadId = ++this.quickFilterContextLoadId
    this.quickFilterContextLoading = true

    try {
      const [terminals, cashAdvances, repaymentRelations] = await Promise.all([
        this.repaymentsService.loadMerchantScopedTerminals(merchantIds, {
          signal: this.lifecycleAbort.signal,
        }),
        this.repaymentsService.loadMerchantScopedCashAdvances(merchantIds, {
          signal: this.lifecycleAbort.signal,
        }),
        context.name
          ? this.repaymentsService.loadMerchantContextRepaymentRelations(
              context.name,
              {
                signal: this.lifecycleAbort.signal,
              },
            )
          : Promise.resolve([]),
      ])

      if (this.destroyed || loadId !== this.quickFilterContextLoadId) {
        return
      }

      this.quickFilterContextRepayments = repaymentRelations
      this.quickFilterContextTerminalOptions = terminals
      this.quickFilterContextCashAdvanceOptions = cashAdvances
      this.quickFilterContextKey = contextKey
      this.quickFilterContextLoaded = true
    } catch (error) {
      if (this.isAbortError(error)) {
        return
      }
      this.quickFilterContextRepayments = []
      this.quickFilterContextTerminalOptions = []
      this.quickFilterContextCashAdvanceOptions = []
      this.quickFilterContextKey = null
      this.quickFilterContextLoaded = false
    } finally {
      if (loadId === this.quickFilterContextLoadId) {
        this.quickFilterContextLoading = false
      }
    }
  }

  private getQuickFilterMerchantContext(): QuickFilterMerchantContext {
    const selectedMerchant = this.selectedMerchants[0]
    const selectedMerchantName = selectedMerchant?.name?.trim()
    const selectedMerchantId = this.getOptionMerchantId(
      'merchant',
      selectedMerchant,
    )

    if (selectedMerchantId || selectedMerchantName) {
      return {
        id: selectedMerchantId,
        name: selectedMerchantName || undefined,
      }
    }

    const inferredMerchantId =
      this.getOptionMerchantId('terminal', this.selectedTerminals[0]) ||
      this.getOptionMerchantId('cashAdvance', this.selectedCashAdvances[0])

    if (!inferredMerchantId) {
      return {}
    }

    return {
      id: inferredMerchantId,
      name: this.findMerchantOptionById(inferredMerchantId)?.name,
    }
  }

  private getCashAdvanceIdsForMerchantContext(
    context: QuickFilterMerchantContext,
  ): Set<string> {
    const ids = new Set<string>()

    this.getCachedQuickFilterOptions('cashAdvance').forEach((option) => {
      if (this.matchesMerchantContextByOption(context, option)) {
        if (option.id) {
          ids.add(String(option.id))
        }
      }
    })

    this.cashAdvanceSearch.results.forEach((option) => {
      if (this.matchesMerchantContextByOption(context, option)) {
        if (option.id) {
          ids.add(String(option.id))
        }
      }
    })

    this.getQuickFilterRelationRepayments().forEach((repayment) => {
      if (
        this.repaymentMatchesMerchantContext(repayment, context) &&
        repayment.cash_advance_id
      ) {
        ids.add(String(repayment.cash_advance_id))
      }
    })

    return ids
  }

  private async getQuickFilterContextMerchantIds(
    context: QuickFilterMerchantContext,
  ): Promise<string[]> {
    const normalizedName = context.name?.trim().toLocaleLowerCase()
    const merchantIds = new Set<string>()

    if (context.id) {
      merchantIds.add(context.id)
    }

    if (normalizedName) {
      this.getCachedQuickFilterOptions('merchant').forEach((option) => {
        if (
          option.id &&
          option.name.trim().toLocaleLowerCase() === normalizedName
        ) {
          merchantIds.add(option.id)
        }
      })

      this.merchantSearch.results.forEach((option) => {
        if (
          option.id &&
          option.name.trim().toLocaleLowerCase() === normalizedName
        ) {
          merchantIds.add(option.id)
        }
      })

      const exactMatches =
        await this.repaymentsService.findMerchantsByExactName(context.name!, {
          signal: this.lifecycleAbort.signal,
        })
      exactMatches.forEach((option) => {
        if (option.id) {
          merchantIds.add(option.id)
        }
      })
    }

    return [...merchantIds]
  }

  private getQuickFilterContextOptions(
    kind: Exclude<QuickFilterKind, 'merchant'>,
  ): QuickFilterOption[] {
    return kind === 'terminal'
      ? this.quickFilterContextTerminalOptions
      : this.quickFilterContextCashAdvanceOptions
  }

  private matchesMerchantContextByOption(
    context: QuickFilterMerchantContext,
    option: QuickFilterOption | undefined,
  ): boolean {
    if (!option) {
      return false
    }

    if (context.id && option.merchantId) {
      return option.merchantId === context.id
    }

    if (context.name && option.name) {
      const merchantName = context.name.trim().toLocaleLowerCase()
      const optionMerchantName = option.merchantId
        ? this.findMerchantOptionById(option.merchantId)
            ?.name?.trim()
            .toLocaleLowerCase()
        : undefined
      return optionMerchantName === merchantName
    }

    return false
  }

  private getMerchantIdFromCashAdvanceId(
    cashAdvanceId: string | undefined,
  ): string | undefined {
    if (!cashAdvanceId) {
      return undefined
    }

    return this.findCashAdvanceOptionById(cashAdvanceId)?.merchantId
  }

  private findCashAdvanceOptionById(
    cashAdvanceId: string,
  ): QuickFilterOption | undefined {
    return (
      this.getCachedQuickFilterOptions('cashAdvance').find(
        (item) => item.id === cashAdvanceId,
      ) ??
      this.cashAdvanceSearch.results.find((item) => item.id === cashAdvanceId)
    )
  }

  private getQuickFilterOptionsFromRepayments(
    kind: Exclude<QuickFilterKind, 'merchant'>,
    context: QuickFilterMerchantContext,
  ): QuickFilterOption[] {
    const options: QuickFilterOption[] = []
    const seen = new Set<string>()

    for (const repayment of this.getQuickFilterRelationRepayments()) {
      if (!this.repaymentMatchesMerchantContext(repayment, context)) {
        continue
      }

      const option =
        kind === 'terminal'
          ? this.normalizeQuickFilterOptions([
              {
                id: repayment.terminal_id,
                name: repayment.terminals?.name,
                merchantId: repayment.merchant_id,
                cashAdvanceId: repayment.cash_advance_id,
              },
            ])[0]
          : this.normalizeQuickFilterOptions([
              {
                id: repayment.cash_advance_id,
                name: repayment.cash_advances?.name,
                merchantId: repayment.merchant_id,
              },
            ])[0]

      if (!option?.name) {
        continue
      }

      const key = option.id ?? option.name.toLocaleLowerCase()
      if (seen.has(key)) {
        continue
      }

      seen.add(key)
      options.push(option)
    }

    return options
  }

  private repaymentMatchesMerchantContext(
    repayment: Repayment,
    context: QuickFilterMerchantContext,
  ): boolean {
    const merchantId = repayment.merchant_id
      ? String(repayment.merchant_id)
      : undefined
    const merchantName =
      typeof repayment.merchants?.name === 'string'
        ? repayment.merchants.name.trim().toLocaleLowerCase()
        : undefined

    if (context.id && merchantId) {
      return merchantId === context.id
    }

    if (context.name && merchantName) {
      return merchantName === context.name.trim().toLocaleLowerCase()
    }

    return false
  }

  private bindQuickFilterScrollListener(
    kind: QuickFilterKind,
    popover: Popover,
  ): void {
    const state = this.getQuickFilterSearchState(kind)
    if (state.scrollCleanup) {
      state.scrollCleanup()
      state.scrollCleanup = null
    }

    setTimeout(() => {
      if (this.destroyed) {
        return
      }

      const container = popover.container?.querySelector(
        '.p-listbox-list-container',
      ) as HTMLElement | null

      if (!container) {
        return
      }

      const onScroll = (event: Event) =>
        this.onQuickFilterListScroll(kind, event)
      container.addEventListener('scroll', onScroll, { passive: true })
      state.scrollCleanup = () => {
        container.removeEventListener('scroll', onScroll)
      }
    }, 0)
  }

  private rebindQuickFilterScrollListenerIfVisible(
    kind: QuickFilterKind,
  ): void {
    const popover = this.quickFilterPopovers[kind]
    if (!popover?.overlayVisible) {
      return
    }

    this.bindQuickFilterScrollListener(kind, popover)
  }
}
